import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, InputAdornment, Chip, Avatar,
    CircularProgress, IconButton, Button, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Alert,
    Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBusinessesFromAPI } from '../../Redux/BusinessDucks';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import AddCommentIcon from '@mui/icons-material/AddComment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import Cookies from 'universal-cookie';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/es';
import { useNavigate } from 'react-router-dom';

dayjs.extend(utc);
dayjs.locale('es');

const getHeaders = () => {
    const cookies = new Cookies();
    return { headers: { Authorization: `Bearer ${cookies.get('token')}` } };
};

// Líneas de negocio del consultor (de la cookie businessLineIds). Super-Admin: vacío (todas).
const getMyBusinessLineIds = () => {
    const cookies = document.cookie.split('; ');
    const blCookie = cookies.find(c => c.startsWith('businessLineIds='));
    if (!blCookie) return [];
    return blCookie.split('=')[1].split(',').map(Number).filter(Boolean);
};
const getMyBusinessLineId = () => {
    const ids = getMyBusinessLineIds();
    return ids.length > 0 ? ids[0] : null;
};

// Parsea líneas "telefono, nombre, rut, email" (separadas por coma o punto y coma) para carga masiva.
const parseBulkLines = (text) => {
    return text.split('\n')
        .map(l => l.trim())
        .filter(Boolean)
        .map(l => {
            const parts = l.split(/[,;]/).map(p => p.trim());
            return { phone: parts[0], name: parts[1] || null, rut: parts[2] || null, email: parts[3] || null };
        })
        .filter(c => c.phone);
};

// ── Diálogo de carga de cartera ─────────────────────────────────────────────────
const CarteraLoadDialog = ({ open, onClose, onLoaded }) => {
    const [tab, setTab] = useState(0);
    const [single, setSingle] = useState({ phone: '', name: '', rut: '', email: '' });
    const [bulkText, setBulkText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type, msg }

    const dispatch = useDispatch();
    const businessLines = useSelector(s => s.business) || [];
    const myBLIds = getMyBusinessLineIds();
    // Consultor: solo sus líneas. Super-Admin (sin líneas en cookie): todas.
    const blOptions = myBLIds.length > 0
        ? businessLines.filter(bl => myBLIds.includes(Number(bl.id)))
        : businessLines;
    // Default: la línea del consultor (cookie). Super-Admin / multi-línea eligen de la lista.
    const [selectedBL, setSelectedBL] = useState(getMyBusinessLineId() || '');

    useEffect(() => {
        if (open) dispatch(fetchBusinessesFromAPI());
    }, [open, dispatch]);

    const reset = () => {
        setSingle({ phone: '', name: '', rut: '', email: '' });
        setBulkText('');
        setFeedback(null);
    };

    const handleClose = () => { reset(); onClose(); };

    const submitSingle = async () => {
        if (!single.phone.trim()) { setFeedback({ type: 'warning', msg: 'El teléfono es requerido.' }); return; }
        if (!selectedBL) { setFeedback({ type: 'error', msg: 'Selecciona una línea de negocio.' }); return; }
        setSubmitting(true);
        setFeedback(null);
        try {
            await axios.post(`${window.API_BASE_URL}/assignments/cartera`,
                { ...single, businessLineId: selectedBL }, getHeaders());
            setFeedback({ type: 'success', msg: 'Contacto agregado a tu cartera.' });
            setSingle({ phone: '', name: '', rut: '', email: '' });
            onLoaded?.();
        } catch (e) {
            if (e?.response?.status === 409) {
                setFeedback({ type: 'error', msg: 'Ese contacto ya pertenece a otro consultor.' });
            } else {
                setFeedback({ type: 'error', msg: 'No se pudo agregar el contacto.' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const submitBulk = async () => {
        const items = parseBulkLines(bulkText);
        if (items.length === 0) { setFeedback({ type: 'warning', msg: 'Ingresa al menos un contacto (uno por línea).' }); return; }
        if (!selectedBL) { setFeedback({ type: 'error', msg: 'Selecciona una línea de negocio.' }); return; }
        setSubmitting(true);
        setFeedback(null);
        try {
            const { data } = await axios.post(`${window.API_BASE_URL}/assignments/cartera/bulk`,
                { businessLineId: selectedBL, items }, getHeaders());
            setFeedback({
                type: 'success',
                msg: `Asignados: ${data.assigned ?? 0} · Nuevos: ${data.created ?? 0} · Omitidos (otro dueño): ${data.skippedOwned ?? 0} · Inválidos: ${data.invalid ?? 0}.`,
            });
            onLoaded?.();
        } catch (e) {
            setFeedback({ type: 'error', msg: 'No se pudo procesar la carga masiva.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Cargar a mi cartera</DialogTitle>
            <DialogContent>
                <Tabs value={tab} onChange={(_, v) => { setTab(v); setFeedback(null); }} sx={{ mb: 2 }}>
                    <Tab label="Un contacto" sx={{ textTransform: 'none' }} />
                    <Tab label="Carga masiva" sx={{ textTransform: 'none' }} />
                </Tabs>

                {feedback && <Alert severity={feedback.type} sx={{ mb: 2 }}>{feedback.msg}</Alert>}

                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="cartera-bl-label">Línea de negocio *</InputLabel>
                    <Select
                        labelId="cartera-bl-label"
                        label="Línea de negocio *"
                        value={selectedBL}
                        onChange={e => setSelectedBL(e.target.value)}
                    >
                        {blOptions.map(bl => (
                            <MenuItem key={bl.id} value={bl.id}>{bl.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {tab === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField label="Teléfono (WhatsApp)" size="small" value={single.phone}
                            onChange={e => setSingle(s => ({ ...s, phone: e.target.value }))} fullWidth required />
                        <TextField label="Nombre" size="small" value={single.name}
                            onChange={e => setSingle(s => ({ ...s, name: e.target.value }))} fullWidth />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField label="RUT" size="small" value={single.rut}
                                onChange={e => setSingle(s => ({ ...s, rut: e.target.value }))} fullWidth />
                            <TextField label="Email" size="small" value={single.email}
                                onChange={e => setSingle(s => ({ ...s, email: e.target.value }))} fullWidth />
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Un contacto por línea. Formato: <strong>telefono, nombre, rut, email</strong> (nombre/rut/email opcionales).
                        </Typography>
                        <TextField
                            multiline minRows={6} fullWidth size="small"
                            placeholder={'56912345678, Juan Pérez\n56987654321, María López, 12345678-9'}
                            value={bulkText}
                            onChange={e => setBulkText(e.target.value)}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
                <Button
                    variant="contained"
                    disabled={submitting}
                    onClick={tab === 0 ? submitSingle : submitBulk}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PersonAddIcon />}
                    sx={{ textTransform: 'none', bgcolor: '#007AFF', '&:hover': { bgcolor: '#0062cc' } }}
                >
                    {tab === 0 ? 'Agregar' : 'Cargar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const fmt = (d) => d ? dayjs.utc(d).local().format('DD/MM/YY HH:mm') : '—';
const fmtPhone = (p) => {
    if (!p) return '';
    const s = String(p);
    return s.length > 4 ? `+(${s.substring(0, 2)}) ${s.substring(2)}` : s;
};

// ── Contact row ────────────────────────────────────────────────────────────────
const ContactRow = ({ contact, isSelected, onClick }) => {
    const initials = (contact.fullName || contact.name || contact.whatsAppId || '?')
        .trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
    const hasActiveConversation = Boolean(
        contact?.activeConversation ||
        contact?.hasActiveConversation ||
        Number(contact?.activeConversationCount || 0) > 0 ||
        Number(contact?.openConversationCount || 0) > 0 ||
        contact?.isActiveNow
    );

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5, cursor: 'pointer',
                borderLeft: isSelected ? '4px solid #007AFF' : '4px solid transparent',
                borderBottom: '1px solid #eef2f7',
                bgcolor: isSelected ? '#f0f7ff' : 'transparent',
                '&:hover': { bgcolor: '#f5f5f5' },
                transition: 'background 0.15s',
            }}
        >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar sx={{ bgcolor: '#007AFF', width: 40, height: 40, fontSize: 14 }}>
                    {initials}
                </Avatar>
                {hasActiveConversation && (
                    <Tooltip title="Conversación activa ahora">
                        <Box sx={{
                            position: 'absolute',
                            right: -1,
                            bottom: -1,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: '#22c55e',
                            border: '2px solid #fff',
                        }} />
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                    {contact.fullName || contact.name || fmtPhone(contact.whatsAppId)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {fmtPhone(contact.whatsAppId)}
                </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Chip
                    label={`${contact.conversationCount} chats`}
                    size="small"
                    sx={{ fontSize: 10, height: 18, bgcolor: '#f0f0f0' }}
                />
                <Typography variant="caption" color="text.secondary" display="block" mt={0.3}>
                    {contact.lastConversationDate ? fmt(contact.lastConversationDate) : '—'}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Conversation history item ──────────────────────────────────────────────────
const ConvItem = ({ conv, onNavigate }) => {
    const bl = conv.accountWSP?.businessLine;
    const blLabel = bl?.name || bl?.nombre || conv?.businessLineName || bl?.siglas;

    return (
        <Box
            onClick={() => onNavigate(conv)}
            sx={{
                px: 2.5,
                py: 1.75,
                mx: 1.75,
                my: 0.9,
                border: '1px solid #cfd6df',
                borderRadius: 1.75,
                cursor: 'pointer',
                bgcolor: '#fff',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                '&:hover': { bgcolor: '#f8fbff', borderColor: '#bcc8d6' },
                transition: 'background 0.15s, border-color 0.15s',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 500 }}>{fmt(conv.startedAt)}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {blLabel && (
                        <Chip
                            label={blLabel}
                            size="small"
                            sx={{ fontSize: 11, height: 22, bgcolor: '#e8f0fe', color: '#1a56db', fontWeight: 700 }}
                        />
                    )}
                    <Chip
                        label={conv.active ? 'Activa' : 'Cerrada'}
                        size="small"
                        sx={{
                            fontSize: 11, height: 24,
                            bgcolor: conv.active ? '#e6f4ea' : '#f5f5f5',
                            color: conv.active ? '#2e7d32' : '#757575',
                            fontWeight: 700,
                        }}
                    />
                    <OpenInNewIcon sx={{ fontSize: 22, color: '#475569' }} />
                </Box>
            </Box>
            {conv.stage && (
                <Box
                    sx={{
                        mt: 0.15,
                        mb: 0.2,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.7,
                        px: 1.1,
                        py: 0.35,
                        borderRadius: 999,
                        border: '1px solid #dbe3ee',
                        bgcolor: '#f4f7fb',
                        maxWidth: '100%',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 10.5,
                            lineHeight: 1.2,
                            fontWeight: 600,
                            letterSpacing: 0.35,
                            textTransform: 'uppercase',
                            color: '#64748b',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Paso actual
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 13.5,
                            lineHeight: 1.2,
                            fontWeight: 600,
                            color: '#334155',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {conv.stage}
                    </Typography>
                </Box>
            )}
            {conv.stageSummary && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.35 }} noWrap display="block">
                    {conv.stageSummary}
                </Typography>
            )}
            {conv.summary && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary', fontStyle: 'italic' }} display="block" mt={0.3}>
                    "{conv.summary}"
                </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }} display="block" mt={0.35}>
                Cuenta: {conv.accountWSP?.number || conv.accountId}
            </Typography>
        </Box>
    );
};

// ── Detail panel ───────────────────────────────────────────────────────────────
const DetailPanel = ({ contact, onClose }) => {
    const [convos, setConvos] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!contact) return;
        setLoading(true);
        axios.get(`${window.API_BASE_URL}/Clients/${contact.id}/conversations`, getHeaders())
            .then(r => {
                const raw = r.data;
                setConvos(Array.isArray(raw) ? raw : (raw?.$values || raw?.items || []));
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [contact]);

    if (!contact) return null;

    const initials = (contact.fullName || contact.name || contact.whatsAppId || '?')
        .trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');

    const handleNavigateToConv = (conv) => {
        navigate('/smart_talking/conversaciones', { state: { conversation: conv } });
    };

    const handleNuevaConversacion = () => {
        navigate('/smart_talking/conversaciones', { state: { newConversationContact: contact } });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
            {/* Header */}
            <Box sx={{ p: 2.5, borderBottom: '1px solid #dfe4ec', display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff' }}>
                <Avatar sx={{ bgcolor: '#007AFF', width: 48, height: 48, fontSize: 16 }}>
                    {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={700} noWrap>
                        {contact.fullName || contact.name || fmtPhone(contact.whatsAppId)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {fmtPhone(contact.whatsAppId)}
                    </Typography>
                </Box>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddCommentIcon sx={{ fontSize: 16 }} />}
                    onClick={handleNuevaConversacion}
                    sx={{
                        borderRadius: 2, fontSize: 12, px: 1.5, py: 0.5,
                        bgcolor: '#007AFF', '&:hover': { bgcolor: '#0062cc' },
                        textTransform: 'none', whiteSpace: 'nowrap',
                    }}
                >
                    Nueva conv.
                </Button>
                <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </Box>

            {/* Info chips */}
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, borderBottom: '1px solid #e4e8ee', bgcolor: '#fff' }}>
                {contact.email && (
                    <Chip icon={<EmailIcon sx={{ fontSize: 14 }} />} label={contact.email} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                )}
                {contact.rut && (
                    <Chip icon={<BadgeIcon sx={{ fontSize: 14 }} />} label={contact.rut} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                )}
                {contact.hubspotId && (
                    <Chip label={`HubSpot: ${contact.hubspotId}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                )}
                <Chip label={`${contact.conversationCount} conversaciones`} size="small"
                    sx={{ fontSize: 11, bgcolor: '#e8f0fe', color: '#1a56db', fontWeight: 600 }} />
                <Chip label={`Desde ${fmt(contact.createdAt)}`} size="small" sx={{ fontSize: 11 }} />
            </Box>

            {/* Conversation history */}
            <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#edf1f5' }}>
                <Typography variant="body2" fontWeight={700} color="text.secondary"
                    sx={{ px: 2.5, py: 1.1, display: 'block', bgcolor: '#fff', borderBottom: '1px solid #dbe2eb', letterSpacing: 0.3 }}>
                    HISTORIAL DE CONVERSACIONES
                </Typography>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : convos.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">Sin conversaciones</Typography>
                    </Box>
                ) : (
                    <Box sx={{ py: 0.9 }}>
                        {convos.map(conv => <ConvItem key={conv.id} conv={conv} onNavigate={handleNavigateToConv} />)}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const ContactosPage = () => {
    const [contacts, setContacts] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);
    const [carteraOpen, setCarteraOpen] = useState(false);
    const PAGE_SIZE = 30;

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [search]);

    const loadContacts = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, pageSize: PAGE_SIZE };
            if (debouncedSearch) params.search = debouncedSearch;
            const r = await axios.get(`${window.API_BASE_URL}/Clients`, { ...getHeaders(), params });
            const raw = r.data;
            const items = Array.isArray(raw) ? raw : (raw?.items || raw?.$values || []);
            setContacts(items);
            setTotal(raw?.total ?? items.length);
        } catch (e) {
            console.error('Error fetching contacts:', e);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, page]);

    useEffect(() => { loadContacts(); }, [loadContacts]);

    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden', bgcolor: '#f7f7fb' }}>
            {/* LEFT: contact list */}
            <Box sx={{
                width: selected ? '38%' : '100%',
                maxWidth: selected ? 420 : '100%',
                display: 'flex', flexDirection: 'column', bgcolor: '#fff',
                transition: 'width 0.2s',
            }}>
                {/* Search bar */}
                <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="h6" fontWeight={700}>Contactos</Typography>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setCarteraOpen(true)}
                            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#007AFF', '&:hover': { bgcolor: '#0062cc' } }}
                        >
                            Cargar a mi cartera
                        </Button>
                    </Box>
                    <TextField
                        fullWidth size="small"
                        placeholder="Buscar por nombre, teléfono, RUT o email..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#999', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5' } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {total} contacto{total !== 1 ? 's' : ''}
                    </Typography>
                </Box>

                {/* List */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {loading ? (
                        Array.from({ length: 8 }).map((_, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#f0f0f0' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ height: 12, bgcolor: '#f0f0f0', borderRadius: 1, mb: 0.5, width: '60%' }} />
                                    <Box sx={{ height: 10, bgcolor: '#f5f5f5', borderRadius: 1, width: '40%' }} />
                                </Box>
                            </Box>
                        ))
                    ) : contacts.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                            <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                            <Typography variant="body2">Sin resultados</Typography>
                        </Box>
                    ) : (
                        contacts.map(c => (
                            <ContactRow
                                key={c.id}
                                contact={c}
                                isSelected={selected?.id === c.id}
                                onClick={() => setSelected(c)}
                            />
                        ))
                    )}
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 1.5, borderTop: '1px solid #f0f0f0' }}>
                        <IconButton size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</IconButton>
                        <Typography variant="caption">{page} / {totalPages}</Typography>
                        <IconButton size="small" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</IconButton>
                    </Box>
                )}
            </Box>

            {/* RIGHT: detail panel */}
            {selected && (
                <>
                    <Box sx={{ width: '1px', bgcolor: '#d2d9e3', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                        <DetailPanel contact={selected} onClose={() => setSelected(null)} />
                    </Box>
                </>
            )}

            <CarteraLoadDialog
                open={carteraOpen}
                onClose={() => setCarteraOpen(false)}
                onLoaded={loadContacts}
            />
        </Box>
    );
};

export default ContactosPage;
